import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { getAllRegions, createRegion, toggleRegionStatus as toggleRegionStatusService, deleteRegion as deleteRegionService, updateRegionOrder as updateRegionOrderService, addStreetToRegion, removeStreetFromRegion, Region } from '../../services/regionService';

const RegionSettings: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [newRegion, setNewRegion] = useState({ name: '' });
  const [showAddRegion, setShowAddRegion] = useState(false);
  const [newStreets, setNewStreets] = useState<Record<string, string>>({});
  const [streetLoading, setStreetLoading] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      const regionsData = await getAllRegions();
      setRegions(regionsData);

    } catch (error) {

      toast.error('خطا در بارگیری مناطق');
    } finally {
      setLoading(false);
    }
  };

  const handleRegionInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewRegion({
      ...newRegion,
      [name]: value
    });
  };

  const addRegion = async () => {
    if (submittingRef.current) return;
    if (newRegion.name.trim() === '') {
      toast.error('نام منطقه الزامی است');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    
    try {
      const newRegionData = await createRegion(newRegion.name.trim());
      toast.success('منطقه با موفقیت ایجاد شد');
      setRegions([newRegionData, ...regions]);
      setNewRegion({ name: '' });
      setShowAddRegion(false);
    } catch (error) {

      toast.error('خطا در ایجاد منطقه');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const toggleRegionStatus = async (id: string) => {
    try {
      const updatedRegion = await toggleRegionStatusService(id);
      toast.success(`منطقه با موفقیت ${updatedRegion.active ? 'فعال' : 'غیرفعال'} شد`);
      setRegions(regions.map(region => 
        region._id === id ? updatedRegion : region
      ));
    } catch (error) {

      toast.error('خطا در تغییر وضعیت منطقه');
    }
  };

  const moveRegionUp = async (region: Region, index: number) => {
    if (index === 0) return;
    
    try {
      await updateRegionOrderService(region._id, 'up');
      
      // Refresh regions to get the updated order
      await fetchRegions();
      
      toast.success('ترتیب منطقه با موفقیت تغییر کرد');
    } catch (error) {

      toast.error('خطا در تغییر ترتیب منطقه');
    }
  };

  const moveRegionDown = async (region: Region, index: number) => {
    if (index === regions.length - 1) return;
    
    try {
      await updateRegionOrderService(region._id, 'down');
      
      // Refresh regions to get the updated order
      await fetchRegions();
      
      toast.success('ترتیب منطقه با موفقیت تغییر کرد');
    } catch (error) {

      toast.error('خطا در تغییر ترتیب منطقه');
    }
  };

  const deleteRegion = async (id: string) => {
    if (!window.confirm('آیا از حذف این منطقه اطمینان دارید؟')) {
      return;
    }
    
    try {
      await deleteRegionService(id);
      toast.success('منطقه با موفقیت حذف شد');
      setRegions(regions.filter(region => region._id !== id));
    } catch (error) {

      toast.error('خطا در حذف منطقه');
    }
  };

  const handleAddStreet = async (regionId: string) => {
    const streetName = newStreets[regionId]?.trim();
    if (!streetName) {
      toast.error('نام خیابان الزامی است');
      return;
    }

    setStreetLoading({ ...streetLoading, [regionId]: true });
    try {
      const updatedRegion = await addStreetToRegion(regionId, streetName);
      setRegions(regions.map(region => 
        region._id === regionId ? updatedRegion : region
      ));
      setNewStreets({ ...newStreets, [regionId]: '' });
      toast.success('خیابان با موفقیت اضافه شد');
    } catch (error: any) {
      toast.error(error.message || 'خطا در اضافه کردن خیابان');
    } finally {
      setStreetLoading({ ...streetLoading, [regionId]: false });
    }
  };

  const handleRemoveStreet = async (regionId: string, streetIndex: number) => {
    try {
      const updatedRegion = await removeStreetFromRegion(regionId, streetIndex);
      setRegions(regions.map(region => 
        region._id === regionId ? updatedRegion : region
      ));
      toast.success('خیابان با موفقیت حذف شد');
    } catch (error: any) {
      toast.error(error.message || 'خطا در حذف خیابان');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">مدیریت مناطق</h1>
        
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">مناطق</h2>
            <button 
              onClick={() => setShowAddRegion(!showAddRegion)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {showAddRegion ? 'انصراف' : 'افزودن منطقه جدید'}
            </button>
          </div>
          
          {showAddRegion && (
            <div className="bg-blue-50 p-4 rounded-md mb-6">
              <h3 className="font-medium text-blue-800 mb-3">افزودن منطقه جدید</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام منطقه</label>
                  <input
                    type="text"
                    name="name"
                    value={newRegion.name}
                    onChange={handleRegionInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="مثال: منطقه ۱۶"
                  />
                </div>
                <div className="flex justify-end space-x-2 space-x-reverse">
                  <button
                    onClick={addRegion}
                    disabled={submitting}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'در حال ارسال...' : 'ثبت منطقه'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {regions.map((region, index) => (
              <div key={region._id} className="bg-gray-50 border rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">{region.name}</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    region.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {region.active ? 'فعال' : 'غیرفعال'}
                  </span>
                                  </div>
                  
                  {/* Streets Management */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">خیابان‌ها:</span>
                      <span className="text-xs text-gray-400">
                        {region.streets?.length || 0} خیابان
                      </span>
                    </div>
                    
                    {/* Display existing streets as tags */}
                    <div className="flex flex-wrap gap-1 mb-2 min-h-[24px]">
                      {region.streets?.map((street, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                        >
                          {street}
                          <button
                            onClick={() => handleRemoveStreet(region._id, index)}
                            className="mr-1 hover:text-red-600 focus:outline-none"
                            title="حذف خیابان"
                          >
                            ×
                          </button>
                        </span>
                      )) || (
                        <span className="text-xs text-gray-400">هیچ خیابانی تعریف نشده</span>
                      )}
                    </div>
                    
                    {/* Add new street */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newStreets[region._id] || ''}
                        onChange={(e) => setNewStreets({
                          ...newStreets,
                          [region._id]: e.target.value
                        })}
                        placeholder="نام خیابان جدید"
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddStreet(region._id);
                          }
                        }}
                      />
                      <button
                        onClick={() => handleAddStreet(region._id)}
                        disabled={streetLoading[region._id]}
                        className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                      >
                        {streetLoading[region._id] ? '...' : 'افزودن'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <button
                      onClick={() => toggleRegionStatus(region._id)}
                      className={`px-3 py-1 rounded text-sm ${
                        region.active 
                          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {region.active ? 'غیرفعال کردن' : 'فعال کردن'}
                    </button>
                    
                    <div className="flex items-center border border-gray-300 rounded">
                      <button
                        onClick={() => moveRegionUp(region, index)}
                        disabled={index === 0}
                        className={`px-2 py-1 text-sm ${
                          index === 0 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-blue-600 hover:bg-blue-50'
                        }`}
                        title="انتقال به بالا"
                      >
                        ↑
                      </button>
                      <div className="w-px h-6 bg-gray-300"></div>
                      <button
                        onClick={() => moveRegionDown(region, index)}
                        disabled={index === regions.length - 1}
                        className={`px-2 py-1 text-sm ${
                          index === regions.length - 1 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-blue-600 hover:bg-blue-50'
                        }`}
                        title="انتقال به پایین"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => deleteRegion(region._id)}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    حذف
                  </button>
                </div>
                
                {region.createdAt && (
                  <div className="mt-2 text-xs text-gray-500">
                    تاریخ ایجاد: {new Date(region.createdAt).toLocaleDateString('fa-IR')}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {regions.length === 0 && (
            <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
              هیچ منطقه‌ای تعریف نشده است. برای شروع، منطقه جدید اضافه کنید.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegionSettings;
